import { get, post, put, del } from "../apiClient";
import { API_ENDPOINTS, API_BASE_URL } from "../config";

const essService = {
  // Get employee profile (self-service)
  getProfile: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.PROFILE);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch profile",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch profile",
      };
    }
  },

  getComplianceDetails: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.COMPLIANCE);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch compliance details'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch compliance details'
      };
    }
  },

  // Update employee profile (self-service)
  updateProfile: async (profileData) => {
    try {
      const response = await put(
        API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.PROFILE,
        profileData
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to update profile",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to update profile",
      };
    }
  },

  // Get employee attendance (self-service)
  getAttendance: async (month) => {
    try {
      const queryString = month ? `?month=${month}` : '';
      const response = await get(`${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.ATTENDANCE}${queryString}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch attendance",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch attendance",
      };
    }
  },

  // Get today's attendance
  getTodayAttendance: async () => {
    try {
      const response = await get(`${API_BASE_URL}/attendance-dashboard/today`);

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          data: response.data || response,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch today's attendance",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch today's attendance",
      };
    }
  },

  // Get attendance dashboard data
  getAttendanceDashboard: async (startDate, endDate) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await get(`${API_BASE_URL}/attendance-dashboard/dashboard${queryString}`);

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          data: response.data || response,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch attendance dashboard",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch attendance dashboard",
      };
    }
  },

  // Clock in
  clockIn: async (location, notes) => {
    try {
      const response = await post(`${API_BASE_URL}/attendance-dashboard/clock-in`, {
        location,
        notes,
        device: { type: 'web_portal' } // Default device type, not displayed to user
      });

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          data: response.data || response,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || response.message || response.error || "Failed to clock in",
          message: response.message || response.responseMessage || response.error,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || error.error || "Failed to clock in",
        message: error.message || error.error,
      };
    }
  },

  // Clock out
  clockOut: async (notes, location, device) => {
    try {
      const response = await post(`${API_BASE_URL}/attendance-dashboard/clock-out`, {
        notes,
        location,
        device
      });

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          data: response.data || response,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to clock out",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to clock out",
      };
    }
  },

  // Start break
  startBreak: async (type = 'lunch', notes = '') => {
    try {
      const response = await post(`${API_BASE_URL}/attendance-dashboard/break-start`, {
        type,
        notes
      });

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          data: response.data || response,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to start break",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to start break",
      };
    }
  },

  // End break
  endBreak: async (notes = '') => {
    try {
      const response = await post(`${API_BASE_URL}/attendance-dashboard/break-end`, {
        notes
      });

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          data: response.data || response,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to end break",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to end break",
      };
    }
  },

  // Get holidays
  getHolidays: async (year, month, type) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (type) params.append('type', type);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await get(`${API_BASE_URL}/holidays/my-holidays${queryString}`);

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          data: response.data || response,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch holidays",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch holidays",
      };
    }
  },

  // Get employee leave balances (self-service)
  getLeaveBalances: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.LEAVE_BALANCES);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch leave balances",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch leave balances",
      };
    }
  },

  // Get employee leave history (self-service)
  getLeaveHistory: async (page = 1, limit = 10, status = 'all', leaveType = 'all') => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        status,
        leaveType
      });
      const response = await get(`${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.LEAVE_HISTORY}?${queryParams.toString()}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch leave history",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch leave history",
      };
    }
  },

  // Get employee leave requests (self-service) - LEGACY (Consider migrating to getLeaveHistory)
  getLeaveRequests: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.LEAVE);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch leave requests",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch leave requests",
      };
    }
  },

  // Apply for leave (self-service)
  applyLeave: async (leaveData) => {
    try {
      const response = await post(
        API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.LEAVE,
        leaveData
      );

      if (response.responseCode === 201 || response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to apply for leave",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to apply for leave",
      };
    }
  },

  // Withdraw leave request (self-service)
  withdrawLeave: async (id) => {
    try {
      const response = await put(
        `${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.LEAVE}/${id}/withdraw`
      );

      if (response.responseCode === 200 || response.success) {
        return {
          success: true,
          message: response.responseMessage || "Leave request withdrawn successfully",
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to withdraw leave request",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to withdraw leave request",
      };
    }
  },

  // Get employee payslips (self-service)
  getPayslips: async (year) => {
    try {
      const queryString = year ? `?year=${year}` : '';
      const response = await get(`${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.PAYSLIPS}${queryString}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch payslips",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch payslips",
      };
    }
  },

  // Get employee training records (self-service)
  getTraining: async (options = {}) => {
    try {
      const { type, page, limit, status, search } = options;
      const params = new URLSearchParams();

      if (type) params.append('type', type);
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      if (status) params.append('status', status);
      if (search) params.append('search', search);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await get(`${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.TRAINING}${queryString}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch training records",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch training records",
      };
    }
  },

  getLearningPaths: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.LEARNING_PATHS);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch learning paths'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch learning paths'
      };
    }
  },

  getQuizAssignment: async (assignmentId) => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.QUIZ_DETAIL(assignmentId));
      if (response.responseCode === 200) {
        return { success: true, data: response.data };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to load quiz assignment'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load quiz assignment'
      };
    }
  },

  submitQuizAttempt: async (assignmentId, payload) => {
    try {
      const response = await post(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.QUIZ_SUBMIT(assignmentId), payload);
      if (response.responseCode === 200) {
        return { success: true, data: response.data };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to submit quiz'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to submit quiz'
      };
    }
  },

  // Get employee performance (self-service)
  getPerformance: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.PERFORMANCE);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch performance data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch performance data",
      };
    }
  },

  // Get resignation requests (self-service)
  getResignationRequests: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.RESIGNATION);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data?.requests || [],
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch resignation requests",
          data: [],
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch resignation requests",
        data: [],
      };
    }
  },

  // Submit resignation (self-service)
  submitResignation: async (resignationData) => {
    try {
      const response = await post(
        API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.RESIGNATION,
        resignationData
      );

      if (response.responseCode === 201 || response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to submit resignation",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to submit resignation",
      };
    }
  },

  // Delete resignation request (self-service)
  deleteResignationRequest: async (requestId, reason) => {
    try {
      const response = await del(`${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.RESIGNATION}/${requestId}`, { data: { reason } });

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to withdraw resignation request",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to delete resignation request",
      };
    }
  },

  // Get document types
  getDocumentTypes: async () => {
    try {
      const response = await get(`${API_BASE_URL}/config/document-types/dropdown`);

      if (response.success || response.responseCode === 200) {
        return {
          success: true,
          data: response.data || [],
        };
      } else {
        return {
          success: false,
          error: response.error || response.responseMessage || "Failed to fetch document types",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch document types",
      };
    }
  },

  // Get employee documents (self-service)
  getDocuments: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.DOCUMENTS);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data || [],
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch documents",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch documents",
      };
    }
  },

  // Update training progress (self-service)
  updateTrainingProgress: async (assignmentId, progressData) => {
    try {
      // Don't send userId - backend will use authenticated user from token
      const response = await post(
        `${API_BASE_URL}/training-assignments/${assignmentId}/progress`,
        progressData
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to update progress",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to update progress",
      };
    }
  },

  // Delete temporary document from Cloudinary
  deleteTempDocument: async (publicId, resourceType) => {
    try {
      const response = await post(`${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.PROFILE}/documents/delete-temp`, {
        publicId,
        resourceType
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to delete document",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to delete document",
      };
    }
  },

  // Upload document to Cloudinary (self-service)
  uploadDocument: async (file, onUploadProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await post(`${API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.PROFILE}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to upload document",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to upload document",
      };
    }
  },

  // Posts services
  getPosts: async () => {
    try {
      const response = await get(`${API_BASE_URL}/posts`);
      if (response.responseCode === 200 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to fetch posts" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  createPost: async (postData) => {
    try {
      const response = await post(`${API_BASE_URL}/posts`, postData);
      if (response.responseCode === 201 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to create post" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  likePost: async (postId) => {
    try {
      const response = await post(`${API_BASE_URL}/posts/${postId}/like`);
      if (response.responseCode === 200 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to like post" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  commentOnPost: async (postId, content) => {
    try {
      const response = await post(`${API_BASE_URL}/posts/${postId}/comment`, { content });
      if (response.responseCode === 201 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to comment on post" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  voteInPoll: async (postId, optionId) => {
    try {
      const response = await post(`${API_BASE_URL}/posts/${postId}/vote`, { optionId });
      if (response.responseCode === 200 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to vote in poll" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  getMentionList: async (search = '') => {
    try {
      const url = search 
        ? `${API_BASE_URL}/posts/mention-list?search=${encodeURIComponent(search)}`
        : `${API_BASE_URL}/posts/mention-list`;
      const response = await get(url);
      if (response.responseCode === 200 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to fetch mention list" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  deletePost: async (postId) => {
    try {
      const response = await del(`${API_BASE_URL}/posts/${postId}`);
      if (response.responseCode === 200 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to delete post" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  updatePost: async (postId, postData) => {
    try {
      const response = await put(`${API_BASE_URL}/posts/${postId}`, postData);
      if (response.responseCode === 200 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to update post" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  getReportingTeam: async () => {
    try {
      const response = await get(API_ENDPOINTS.EMPLOYEE_SELF_SERVICE.REPORTING_TEAM);
      if (response.responseCode === 200 || response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || "Failed to fetch reporting team" };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }
};

export default essService;

